import { forEach, filter } from 'ramda';
import { Project } from '../Components/Projects/projects';
import { httpFetch } from '../http';
import { DefaultSettings } from './constants';
import { projectsApi } from '../api/projectsApi';
import { migrateProject } from './projectMigrations';
import { createDefaultFieldSelection, normalizeFieldSelection } from './fieldSelection';
import { cacheTemplateProjectString, getDefensiveTemplateFallbackData, isDefensiveTemplateShareCode } from './templateCache';
import { mergeApiMetadataAfterMigration } from './projectApiMetadata';
import { applyProjectVersionDefaults } from './projectVersions';

/**
 * Returned a random number between a specified minimum and maximum number
 * @param min (number)
 * @param max (number)
 * @returns {*}
 */
export const randomBetween = (min: number, max: number) => Math.floor(Math.random() * (max - min)) + min;

/**
 * Works like useState in React, but Javascript not in ReactFunctional componenet.
 * @param {any} defaultValue any starting value for the state
 */
export const vanillaUseState = <T>(defaultValue: T) => {
  let value = defaultValue;
  const getValue = () => value;
  const setValue = (newValue: T) => {
    value = newValue;
  };
  return [getValue, setValue];
};

export const generateBlob = (strings: Array<string>) => new Blob(strings);

const colorRegex = { r: /Red/, g: /Green/, b: /Blue/ };

type LEDKey = string;
type LEDMapping = Record<LEDKey, number>;

export const getLEDObjects = (toy: { LED: LEDMapping }, led: string, colorObject: { r: number; g: number; b: number }): Record<number, number> => {
  const ledObject: Record<number, number> = {};
  const checkKeys = (key: string) => {
    if (colorRegex.r.test(key)) {
      ledObject[toy.LED[key]] = colorObject.r;
    } else if (colorRegex.g.test(key)) {
      ledObject[toy.LED[key]] = colorObject.g;
    } else if (colorRegex.b.test(key)) {
      ledObject[toy.LED[key]] = colorObject.b;
    }
  };

  const filterKeys = (key: string) => key.toLowerCase().includes(led.toLowerCase());
  const keys = Object.keys(toy.LED);

  if (led === 'MAIN') {
    forEach(checkKeys, keys);
  } else {
    const filtered = filter(filterKeys, keys);
    forEach(checkKeys, filtered);
  }

  console.log('toy', toy);
  console.log('led', led);
  console.log('color object', colorObject);
  console.log('led object:', ledObject);

  return ledObject;
};

export const rgbaToRgb = (color: { a: number; r: number; g: number; b: number }) => {
  if (color) {
    if (color.a) {
      return {
        r: color.r * color.a,
        g: color.g * color.a,
        b: color.b * color.a,
      };
    }
    // sometimes a can be undefined
    return { r: color.r, b: color.b, g: color.g };
  }
  return DefaultSettings.color;
};

// For some reason the autogen for the JavaScript SDK converts the ints to strings.
// All other clients just pass the int. Get the int to pass to the vm.
export const getControllID = (stringID: string) => {
  switch (stringID) {
    case 'decelerating_stop':
      return 0;
    case 'raw_motor':
      return 1;
    case 'tank_drive':
      return 2;
    case 'drive_with_yaw_advanced_mode':
      return 3;
    case 'drive_with_yaw_basic_mode':
      return 4;
    case 'rc_drive_rate_mode':
      return 5;
    case 'rc_drive_slew_mode':
      return 6;
    case 'xy_position_drive':
      return 7;
    case 'infrared_follow_and_evade':
      return 8;
    case 'magnetometer_calibration':
      return 9;
    case 'simple_stop':
      return 10;
    case 'back_emf_calibration':
      return 11;
    case 'rc_drive_advanced_mode':
      return 12;
    case 'heading_hold_stop':
      return 13;
    case 'rc_drive_radius_mode':
      return 14;
    case 'temporospatial_vector_drive':
      return 15;
  }
  return undefined;
};

/** Replaces characters invalid in Windows/macOS/Linux file and zip entry names. */
export const checkForUnsupportedCharacters = (title: string) => title.replace(/[\\/:*?"<>|]/g, '_');

const stripLegacyProjectType = (project: Project): Project => {
  const projectWithoutType = { ...project } as Project & { type?: unknown };
  delete projectWithoutType.type;
  return projectWithoutType;
};

export const createProjectFromPlatformCode = async (name: string, code: string): Promise<Project> => {
  let projectString: string | null = null;
  let apiMetadata: unknown = {};

  try {
    const response = await projectsApi.get(code);
    projectString = response.project_string;
    apiMetadata = response;

    // Keep a defensive local copy for critical starters (python api + custom bot).
    if (isDefensiveTemplateShareCode(code)) {
      cacheTemplateProjectString(code, response.project_string, response.updated_at, response);
    }
  } catch (err) {
    if (isDefensiveTemplateShareCode(code)) {
      const cachedTemplate = getDefensiveTemplateFallbackData(code);
      projectString = cachedTemplate?.projectString ?? null;
      apiMetadata = cachedTemplate?.apiMetadata ?? {};
      if (projectString) {
        cacheTemplateProjectString(code, projectString, undefined, apiMetadata);
      }
    }
    if (!projectString) throw err;
  }

  const parsed = stripLegacyProjectType(JSON.parse(projectString) as Project);

  // Migrate from the serialized body first so envelope CURRENT cannot skip path upgrades.
  const migrated = migrateProject({
    ...parsed,
    name: name,
    lastModified: new Date().toISOString(),
    id: crypto.randomUUID(),
    selectedField: parsed.selectedField ? normalizeFieldSelection(parsed.selectedField) : createDefaultFieldSelection(),
  });

  return applyProjectVersionDefaults(mergeApiMetadataAfterMigration(migrated as Project, apiMetadata)) as Project;
};

export const loadExamples = async (): Promise<Project[]> => {
  // Example files are in public/examples/ and served statically
  const exampleFiles = ['Classic Loop.bps', 'Hello World.bps', 'List and Loop.bps', 'One Time Execution.bps'];

  const examples = await Promise.all(
    exampleFiles.map(async (filename) => {
      try {
        const response = await httpFetch(`/examples/${filename}`, { responseType: 'json' });
        if (!response.ok) {
          console.warn(`Failed to load example: ${filename}`);
          return null;
        }
        return (await response.json()) as Project;
      } catch (error) {
        console.error(`Error loading example ${filename}:`, error);
        return null;
      }
    })
  );

  // Filter out any failed loads
  return examples.filter((example): example is Project => example !== null);
};

export const createProjectFromExample = (example: Project) => {
  const project = stripLegacyProjectType(example);
  // Migrate before defaults so versionless examples still run path upgrades.
  const newProject = {
    ...project,
    name: `Copy of ${project.name}`,
    lastModified: new Date().toISOString(),
    id: crypto.randomUUID(),
    selectedField: project.selectedField ? normalizeFieldSelection(project.selectedField) : createDefaultFieldSelection(),
  };

  return applyProjectVersionDefaults(migrateProject(newProject)) as Project;
};

/** True when the pathname is editor, dashboard, or path builder for a loaded project. */
export function isProjectViewPathname(pathname: string): boolean {
  return pathname.includes('/editor') || pathname.includes('/dashboard') || pathname.includes('/pathbuilder');
}
